import { Component, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
    ShButtonComponent,
    ShInputTextComponent,
    ShSelectComponent,
    ShSelectOption,
    SHUI_MODAL_CONFIG,
    ShuiModalConfig,
    ShuiModalRef,
    TextValueAccessor
} from '@gbr-monorepo/gbr-shui';
import { TranslateModule } from '@ngx-translate/core';
import { GraphUser, Roles, User } from '@gbr-monorepo/interfaces';
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs';
import { UserSearchComponent } from '../../../../user-search/components/user-search/user-search.component';
import { DEFAULT_USER_SEARCH_TEXT_CONFIG } from '../../../../user-search/configs/user-search-text.config';
import { GraphUserServiceContract } from '../../services/graph-user-service.contract';

const DIRECTORY_LOOKUP_DEBOUNCE_MS = 400;

@Component({
    selector: 'app-add-user-dialog',
    templateUrl: './add-user.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        ShInputTextComponent,
        ShSelectComponent,
        ShButtonComponent,
        TextValueAccessor,
        TranslateModule,
        UserSearchComponent
    ]
})
export class AddUserComponent {
    private readonly config = inject<ShuiModalConfig<{ delegatesEnabled: boolean }>>(SHUI_MODAL_CONFIG);
    private readonly dialogRef = inject<ShuiModalRef<{ confirmed: boolean; payload?: User }>>(ShuiModalRef);
    private readonly directoryUserService = inject(GraphUserServiceContract);
    private readonly formBuilder = inject(FormBuilder);
    private readonly destroyRef = inject(DestroyRef);
    protected readonly DEFAULT_USER_SEARCH_TEXT_CONFIG = DEFAULT_USER_SEARCH_TEXT_CONFIG;
    private readonly delegatesEnabled = !!this.config.data?.delegatesEnabled;
    protected readonly roleOptions: ShSelectOption[] = Object.values(Roles)
        .filter(role => this.delegatesEnabled || role !== Roles.DELEGATE)
        .map(role => ({ value: role, label: role }));

    protected readonly addUserForm = this.formBuilder.nonNullable.group({
        gid: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(8)]],
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        email: [
            '',
            [Validators.required, Validators.email, Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{3,4}$')]
        ],
        roles: [[] as Roles[], Validators.required],
        orgCode: ['']
    });

    constructor() {
        this.watchDirectoryLookup('gid', value => this.directoryUserService.findUsers({ employeeId: value }));
        this.watchDirectoryLookup('email', value => this.directoryUserService.findUsers({ mail: value }));
    }

    private watchDirectoryLookup(
        field: 'gid' | 'email',
        lookup: (value: string) => ReturnType<GraphUserServiceContract['findUsers']>
    ): void {
        const control = this.addUserForm.controls[field];
        control.valueChanges
            .pipe(
                debounceTime(DIRECTORY_LOOKUP_DEBOUNCE_MS),
                distinctUntilChanged(),
                filter(() => control.valid),
                switchMap(value => lookup(value)),
                map(response => response.items),
                filter((items): items is [GraphUser] => items.length === 1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(([user]) => this.applyDirectoryMatch(field, user));
    }

    private applyDirectoryMatch(field: 'gid' | 'email', user: GraphUser): void {
        const fieldMap: Partial<User> = {
            firstName: user.givenName,
            lastName: user.surname,
            orgCode: user.department
        };
        if (field === 'gid') {
            fieldMap.email = user.mail;
        } else {
            fieldMap.gid = user.employeeId ?? undefined;
        }
        this.addUserForm.patchValue(fieldMap);
    }

    protected createUser(): void {
        const userToCreate: User = {
            gid: this.addUserForm.controls['gid'].value,
            firstName: this.addUserForm.controls['firstName'].value,
            lastName: this.addUserForm.controls['lastName'].value,
            roles: this.addUserForm.controls['roles'].value,
            email: this.addUserForm.controls['email'].value,
            orgCode: this.normalizeOptionalString(this.addUserForm.controls['orgCode'].value)
        };
        this.dialogRef.close({ confirmed: true, payload: userToCreate });
    }

    protected cancel(): void {
        this.dialogRef.close({ confirmed: false });
    }

    protected onUserSelect({
        employeeId: gid,
        givenName: firstName,
        surname: lastName,
        mail: email,
        department: orgCode
    }: GraphUser): void {
        this.addUserForm.patchValue({ gid: gid ?? '', firstName, lastName, email, orgCode });
    }

    private normalizeOptionalString(value: string | null | undefined): string | undefined {
        const normalized = value?.trim();
        return normalized ? normalized : undefined;
    }
}
